import { status as AppointmentStatuses } from '@spike/appointment-model';
import {
    INITIAL_BOOKING_EDIT_SCOPE_REQUEST,
    NewBookingsStatus,
    saveCreateAppointmentThunk,
    updateBookingAppointmentsThunk
} from '@spike/new-bookings-action';
import { showSuccess } from '@spike/notifications-action';
import { Order } from '@spike/order-model';
import { OrdersStatus, reloadOrderThunk } from '@spike/orders-action';
import { NewAppointment as NewAppointmentModel } from '@spike/new-booking-model';
import useNonInitialEffect from '@versiondos/hooks';
import PetAndServices from 'components/CreateBooking/PetAndServices';
import { useApiClientWrapper, useAuth, useMarketplace, useMasterData, useTimeZone } from 'hooks';
import { NewAppointment } from 'model/NewBooking';
import {
    Boarding,
    Daycare,
    Feeding,
    Medication,
    Discount as MultipleBookingDiscount,
    PetServiceWrapper,
    Training,
    Transportation,
    Walking,
    versionPetAndServices
} from 'model/NewMultipleBooking';
import { resolveBookingType } from 'model/Service';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { convertDiscount, getUsedPetIds } from './EditOrderItem';
import { getOrderStartTime } from 'components/Checkout2/utils/OrderUtils';
import { getRoomUnitId } from 'components/CreateBooking/CreateBooking';
import { getPetParentId } from './AddOrderItem';
import { Task } from '@spike/task-model';
import {
    createTaskDate,
    createMedicationTaskDate,
    getRangeDateByMomentTypeIdWithTimezone,
    shouldCreateTaskForDay
} from 'utils/dateTimeUtils';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';
import { DaySchedule } from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/MedicalSchedule/MedicalSchedule';
import { buildDaycareAppointments } from './buildDaycareAppointments';
import { buildWalkingAppointments } from './buildWalkingAppointments';
import { buildTrainingAppointments } from './buildTrainingAppointments';
import { buildTransportationAppointments } from './buildTransportationAppointments';
import {
    resolveDaycareSavePricing,
    resolveBoardingOrderItemPriceForSave,
    resolveDiscountPayloadForSave,
    resolveOrderItemPriceForSave,
    resolveStandardServiceSavePricing
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import {
    customPriceDataFromBoarding,
    customPriceDataFromDaycare,
    customPriceDataFromPetService,
    customPriceDataFromTraining,
    customPriceDataFromTransportation,
    customPriceDataFromWalking,
    persistOrderItemCustomPrices
} from 'components/CreateBooking/utils/bookingCustomPrices';
import { hasMultipleDaycareDates } from 'utils/daycareDateSlots';
import { hasMultipleWalkingDates } from 'utils/walkingDateSlots';
import { hasMultipleTrainingDates } from 'utils/trainingDateSlots';
import { hasMultipleTransportationDates } from 'utils/transportationDateSlots';

const getSpecificFeedingMomentIds = (f: Feeding): number[] =>
    f.momentTypes && f.momentTypes.length > 0 ? f.momentTypes : Object.keys(f.specificMomentTimes || {}).map(Number);

const feedingIsSpecificTimeOnSave = (f: Feeding): boolean =>
    f.feedingScheduleType === 'specificTime' &&
    getSpecificFeedingMomentIds(f).some(m => !!String(f.specificMomentTimes?.[m] ?? '').trim());

export interface AddPetProps {
    order: Order;
    bookingId: number;
    recurrentInfo?: boolean;
    inRecurringSeries?: boolean;
    className?: string;
    onClose: () => void;
}

export const AddPet: FunctionComponent<AddPetProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const auth = useAuth();
    const timezone = useTimeZone();

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const [saving, setSaving] = useState(false);

    const saveAppointments = (entries: Array<{ appointment: NewAppointmentModel; enrollmentId?: number }>) => {
        if (props.inRecurringSeries && entries.every(entry => entry.enrollmentId == null)) {
            dispatch(
                updateBookingAppointmentsThunk(
                    apiClientWrapper,
                    entries.map(entry => entry.appointment),
                    false,
                    INITIAL_BOOKING_EDIT_SCOPE_REQUEST
                )
            );
            setSaving(false);
            props.onClose();
            return;
        }

        entries.forEach(({ appointment, enrollmentId }) =>
            dispatch(
                saveCreateAppointmentThunk(
                    apiClientWrapper,
                    appointment,
                    enrollmentId != null ? 'Enrollment' : undefined,
                    enrollmentId
                )
            )
        );
    };

    const updateAppointments = (appointments: Array<NewAppointmentModel>) => {
        dispatch(
            updateBookingAppointmentsThunk(
                apiClientWrapper,
                appointments,
                false,
                props.inRecurringSeries ? INITIAL_BOOKING_EDIT_SCOPE_REQUEST : undefined
            )
        );
        if (!props.inRecurringSeries) {
            dispatch(showSuccess('Appointments are being created'));
        }
    };

    const { morning, afternoon, evening } = getFeedingSlotTimes(marketplace, masterData);
    const bookAt = getOrderStartTime(props.order) ?? moment();

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.Error:
                setSaving(false);
                break;
        }
    }, [ordersStatus]);

    useEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SavingCreateAppointmentSuccess: {
                setSaving(false);
                props.onClose();
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            }
            case NewBookingsStatus.Error:
                setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newBookingsStatus]);

    const saveHandler = (
        date: Moment | undefined,
        petId: number | undefined,
        service: PetServiceWrapper | undefined,
        staffId: number | undefined,
        discount: MultipleBookingDiscount | undefined
    ) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromPetService(service));
        if (date && petId && service && auth.user && props.order.client.id) {
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = service.enrollment?.id;
            const membershipDiscount = resolveDiscountPayloadForSave(discount, service);
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: date.clone(),
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: service?.service.id,
                staff_id: staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: membershipDiscount?.amount,
                discount_percentage: membershipDiscount?.percentage,
                order_item_price: resolveOrderItemPriceForSave(service)
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveDaycareHandler = (
        daycare: Daycare,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petId: number
    ) => {
        if (!auth.user) {
            return;
        }

        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromDaycare(daycare));

        const fallbackDate = daycare.from || bookAt;

        if (hasMultipleDaycareDates(daycare, fallbackDate, timezone)) {
            const appointments = buildDaycareAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                daycare,
                feedings,
                medications,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                feedingSlots: { morning, afternoon, evening },
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        const items: Array<Task> = [];

        const createFeedingTasksForDay = (dayLocal: Moment) => {
            feedings?.forEach((feeding: Feeding) => {
                if (feedingIsSpecificTimeOnSave(feeding)) {
                    getSpecificFeedingMomentIds(feeding).forEach(momentTypeId => {
                        const timeString = feeding.specificMomentTimes?.[momentTypeId];
                        if (!timeString?.trim()) return;
                        items.push({
                            taskDate: createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone),
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId,
                                specificTime: true
                            }
                        });
                    });
                } else {
                    feeding.momentTypes?.forEach((momentTypeId: number) => {
                        const taskDate = createTaskDate(
                            dayLocal,
                            momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                            timezone
                        );

                        items.push({
                            taskDate: taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId
                            }
                        });
                    });
                }
            });
        };

        const createMedicationTasksForDay = (dayLocal: Moment) => {
            medications?.forEach((medication: Medication) => {
                medication.schedule?.forEach((scheduleTime: string) => {
                    const taskDate = createMedicationTaskDate(dayLocal, scheduleTime, timezone);

                    items.push({
                        taskDate: taskDate,
                        medication: {
                            taskType: 2 as const,
                            medicationName: medication.name,
                            medicationMethodId: medication.methodType,
                            dosageAmount: medication.dosage.quantity?.toString(),
                            dosageUnitId: medication.dosage.unit,
                            medicationInstructions: medication.instructions,
                            roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId
                        }
                    });
                });
            });
        };

        const dayLocal = daycare.from.clone().tz(timezone);
        createFeedingTasksForDay(dayLocal);
        createMedicationTasksForDay(dayLocal);

        if (daycare.from && petId && auth.user) {
            const appointmentUuid = apiClientWrapper.uuid();
            const { orderItemPrice, discountAmount, discountPercentage, enrollmentId } =
                resolveDaycareSavePricing(daycare);
            const newAppointment: NewAppointmentModel = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: daycare.from.clone(),
                booking_end_at: daycare.to?.clone() || daycare.from.clone().add(8, 'hours'),
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: daycare.service?.serviceId!,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: discountAmount,
                discount_percentage: discountPercentage,
                order_item_price: orderItemPrice,
                room_unit_id: getRoomUnitId(daycare.room) || daycare.roomUnitId,
                tasks: items,
                notes: ''
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveBoardingHandler = (
        boarding: Boarding,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petId: number
    ) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromBoarding(boarding));

        const items: Array<Task> = [];
        const isMultiDay = Boolean(boarding.from && boarding.to && boarding.to.isAfter(boarding.from));

        const createFeedingTasksForDay = (dayLocal: Moment) => {
            feedings.forEach(feeding => {
                if (feedingIsSpecificTimeOnSave(feeding)) {
                    getSpecificFeedingMomentIds(feeding).forEach(momentTypeId => {
                        const timeString = feeding.specificMomentTimes?.[momentTypeId];
                        if (!timeString?.trim()) return;
                        const taskDate = createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone);
                        if (isMultiDay) {
                            if (taskDate.isBefore(boarding.from) || taskDate.isSameOrAfter(boarding.to!)) {
                                return;
                            }
                        }
                        items.push({
                            taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room),
                                specificTime: true
                            }
                        });
                    });
                } else {
                    feeding.momentTypes?.forEach(momentTypeId => {
                        const taskDate = createTaskDate(
                            dayLocal,
                            momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                            timezone
                        );
                        const rangeDate = getRangeDateByMomentTypeIdWithTimezone(
                            dayLocal,
                            momentTypeId,
                            morning,
                            afternoon,
                            evening,
                            timezone
                        );

                        if (isMultiDay) {
                            if (
                                !shouldCreateTaskForDay(
                                    taskDate,
                                    boarding.from,
                                    boarding.to!,
                                    rangeDate.from,
                                    rangeDate.to
                                )
                            ) {
                                return;
                            }
                        }

                        items.push({
                            taskDate: taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room)
                            }
                        });
                    });
                }
            });
        };

        const createMedicationTasksForDay = (dayLocal: Moment) => {
            medications.flatMap(medication =>
                medication.schedule?.map(schedule => {
                    const taskDate = createMedicationTaskDate(dayLocal, schedule, timezone);

                    if (isMultiDay) {
                        if (taskDate.isBefore(boarding.from) || taskDate.isSameOrAfter(boarding.to)) {
                            return;
                        }
                    }

                    items.push({
                        taskDate,
                        medication: {
                            taskType: 2 as const,
                            medicationName: medication.name,
                            medicationMethodId: medication.methodType,
                            dosageAmount: medication.dosage.quantity?.toString(),
                            dosageUnitId: medication.dosage.unit,
                            medicationInstructions: medication.instructions,
                            roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room)
                        }
                    });
                })
            );
        };

        const createSpecificMedicationTasks = () => {
            medications.flatMap((medication: Medication) => {
                medication.specificSchedule?.forEach((scheduleTime: DaySchedule) => {
                    if (scheduleTime.enabled) {
                        scheduleTime.doses?.forEach((dose: string) => {
                            const taskDate = createMedicationTaskDate(scheduleTime.date, dose, timezone);

                            items.push({
                                taskDate,
                                medication: {
                                    taskType: 2 as const,
                                    medicationName: medication.name,
                                    medicationMethodId: medication.methodType,
                                    dosageAmount: medication.dosage.quantity?.toString(),
                                    dosageUnitId: medication.dosage.unit,
                                    medicationInstructions: medication.instructions,
                                    roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room)
                                }
                            });
                        });
                    }
                });
            });
        };

        if (isMultiDay) {
            const start = boarding.from.clone().tz(timezone).startOf('day');
            const end = boarding.to.clone().tz(timezone).startOf('day');
            const totalDays = end.diff(start, 'days');
            for (let i = 0; i <= totalDays; i++) {
                const currentDay = start.clone().add(i, 'days');
                createFeedingTasksForDay(currentDay);
                createMedicationTasksForDay(currentDay);
            }
            createSpecificMedicationTasks();
        } else {
            const dayLocal = boarding.from.clone().tz(timezone);
            createFeedingTasksForDay(dayLocal);
            createMedicationTasksForDay(dayLocal);
        }

        if (boarding.from && petId && auth.user) {
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = boarding.enrollment?.id;
            const newAppointment: NewAppointmentModel = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: boarding.from.clone(),
                booking_end_at: boarding.to?.clone() || boarding.from.clone().add(8, 'hours'),
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: boarding.service?.serviceId!,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                order_item_price: resolveBoardingOrderItemPriceForSave(boarding),
                room_unit_id: boarding.roomUnitId || getRoomUnitId(boarding.room),
                tasks: items,
                notes: '',
                details: {
                    boarding: {
                        room: boarding.room?.id,
                        notes: boarding.notes,
                        selectedOffer: boarding.service?.selectedOffer
                    }
                }
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveTransportationHandler = (transportation: Transportation, petId: number) => {
        if (!auth.user || !transportation.service) return;

        persistOrderItemCustomPrices(
            dispatch,
            apiClientWrapper,
            petId,
            customPriceDataFromTransportation(
                transportation,
                transportation.pickup?.time?.from || transportation.dropOff?.time?.from || bookAt,
                timezone
            )
        );

        const fallbackDate = transportation.pickup?.time?.from || transportation.dropOff?.time?.from || bookAt;

        if (hasMultipleTransportationDates(transportation, fallbackDate, timezone)) {
            const appointments = buildTransportationAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                transportation,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        const appointments = buildTransportationAppointments({
            apiClientWrapper,
            bookingId: props.bookingId,
            transportation,
            petId,
            customerId: getPetParentId(props.order, petId),
            createdByStaffId: auth.user.staffId,
            fallbackDate,
            timezone,
            status: AppointmentStatuses.BOOKED,
            marketplaceAddressId: marketplace.basics.address.id as number
        });

        saveAppointments(
            appointments.map(app => ({
                appointment: app,
                enrollmentId: (app as { redeemId?: number }).redeemId ?? transportation.enrollment?.id
            }))
        );
    };

    const saveWalkingHandler = (walking: Walking, petId: number | undefined) => {
        if (!auth.user || !petId || !walking.service) {
            return;
        }

        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromWalking(walking));

        const fallbackDate = walking.from || bookAt;

        if (hasMultipleWalkingDates(walking, fallbackDate, timezone)) {
            const appointments = buildWalkingAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                walking,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        if (walking.from) {
            const bookedAt = walking.from.clone().seconds(0).milliseconds(0);
            const bookingEndAt = walking.to
                ? walking.to.clone().seconds(0).milliseconds(0)
                : bookedAt.clone().add(walking.service?.duration || 0, 'minutes');
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = walking.enrollment?.id;
            const walkingItemDiscount: MultipleBookingDiscount = {
                type: (walking.discount?.type === 'percentage' ? 'percentage' : 'amount') as 'amount' | 'percentage',
                value: walking.discount?.value ?? 0
            };
            const walkingPricing = resolveStandardServiceSavePricing({
                price: walking.price || walking.service?.price || 0,
                enrollment: walking.enrollment,
                membershipItem: walking.membershipItem,
                discount: walkingItemDiscount,
                businessArea: 'walking'
            });
            const newAppointment: NewAppointmentModel = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: bookedAt,
                booking_end_at: bookingEndAt,
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: walking.service?.serviceId!,
                staff_id: walking?.staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: walkingPricing.discountAmount,
                discount_percentage: walkingPricing.discountPercentage,
                order_item_price: walkingPricing.orderItemPrice,
                details: {
                    walking: {
                        type: walking.service?.inHomeService ? 'in_home' : 'brick_and_mortar',
                        address: walking.address || '',
                        duration: walking.service?.duration || 0,
                        instructions: walking.service?.inHomeService ? walking.instructions : undefined
                    }
                },
                service_session_id: walking.serviceSessionId ?? null
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const usedPetIds: Array<number> = useMemo(() => getUsedPetIds(props.order), [props.order]);

    const saveTrainingHandler = (training: Training, petId: number | undefined) => {
        if (!auth.user || !petId || !training.service) {
            return;
        }

        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromTraining(training));

        const fallbackDate = training.from || bookAt;

        if (hasMultipleTrainingDates(training, fallbackDate, timezone)) {
            const appointments = buildTrainingAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                training,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        if (training.from) {
            const bookedAt = training.from.clone().seconds(0).milliseconds(0);
            const bookingEndAt = training.to
                ? training.to.clone().seconds(0).milliseconds(0)
                : bookedAt.clone().add(training.service?.duration || 0, 'minutes');
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = training.enrollment?.id;
            const trainingItemDiscount: MultipleBookingDiscount = {
                type: (training.discount?.type === 'percentage' ? 'percentage' : 'amount') as 'amount' | 'percentage',
                value: training.discount?.value ?? 0
            };
            const trainingPricing = resolveStandardServiceSavePricing({
                price: training.price || training.service?.price || 0,
                enrollment: training.enrollment,
                membershipItem: training.membershipItem,
                discount: trainingItemDiscount,
                businessArea: 'training'
            });
            const newAppointment: NewAppointmentModel = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: bookedAt,
                booking_end_at: bookingEndAt,
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: training.service?.serviceId!,
                staff_id: training?.staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: trainingPricing.discountAmount,
                discount_percentage: trainingPricing.discountPercentage,
                order_item_price: trainingPricing.orderItemPrice,
                details: {
                    training: {
                        type: training.service?.inHomeService ? 'in_home' : 'brick_and_mortar',
                        address: training.address || '',
                        duration: training.service?.duration || 0,
                        instructions: training.service?.inHomeService ? training.instructions : undefined,
                        sessionType: resolveBookingType(training.service?.bookingType)
                    }
                },
                service_session_id: training.serviceSessionId ?? null
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    return (
        <PetAndServices
            parentId={props.order.client.id!}
            date={bookAt}
            versionPetAndServices={versionPetAndServices.another}
            petsUsed={usedPetIds}
            discount={convertDiscount()}
            loading={saving}
            onBack={props.onClose}
            flow={props.recurrentInfo ? 'grooming' : undefined}
            recurrentInfo={props.recurrentInfo}
            onBook={saveHandler}
            onSaveDaycare={saveDaycareHandler}
            onSaveBoarding={saveBoardingHandler}
            onSaveTransportation={saveTransportationHandler}
            onSaveWalking={saveWalkingHandler}
            onSaveTraining={saveTrainingHandler}
        />
    );
};

export default AddPet;
